/**
 * useOfflineSync — Hook para sincronización automática de datos offline
 *
 * Maneja DOS colas distintas:
 *
 * 1. inspectorSegments  → segmentos de jornada PE guardados sin conexión
 * 2. inspectorActionQueue → acciones del inspector (aprobar/rechazar incidencia,
 *    validar segmento PI) guardadas sin conexión, incluyendo fotos serializadas
 *
 * Se sincroniza automáticamente (al montar, cada 30s, al volver a primer plano y
 * cuando regresa la señal) y también manualmente con `syncNow()`.
 *
 * Cada sincronización genera un REPORTE de diagnóstico (`lastReport`) que explica,
 * en lenguaje claro, qué pasó y cuáles son las causas probables si algo falló.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { storageService } from '../services/storageService';
import { fetchWithRetry, inspectorService } from '../services/inspectorService';

const ALREADY_PROCESSED_RE = /ya fue|ya evalu|ya valid|ya existe/;
// Tras una falla, los reintentos AUTOMÁTICOS se espacian para no saturar el servidor
const AUTO_RETRY_COOLDOWN_MS = 2 * 60 * 1000;

/** Lee la fecha de expiración (ms) de un JWT, o null si no se puede leer. */
function getTokenExpiryMs(token) {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const data = JSON.parse(atob(payload));
    return data.exp ? data.exp * 1000 : null;
  } catch (e) {
    return null;
  }
}

/** Convierte el detalle del servidor (string o arreglo de FastAPI) en texto. */
function detailToText(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  try {
    return Array.isArray(detail)
      ? detail.map(d => d.msg || JSON.stringify(d)).join('; ')
      : JSON.stringify(detail);
  } catch (e) {
    return String(detail);
  }
}

/** Explica una respuesta HTTP fallida con las causas más probables. */
function explainHttpFailure(status, detail) {
  const d = detailToText(detail);
  switch (status) {
    case 401:
      return { auth: true, text: 'Tu sesión expiró o el token ya no es válido. Cierra sesión y vuelve a entrar; las validaciones guardadas en el teléfono NO se borran y se enviarán después.' };
    case 403:
      return { text: `El servidor no te dio permiso (${d || '403'}). Probable: el recorrido es de otra región o tu usuario cambió de permisos. Avisa a tu supervisor.` };
    case 404:
      return { text: `El servidor no encontró el recorrido/segmento (${d || '404'}). Probable: el recorrido fue reasignado, eliminado o regenerado. Avisa a soporte; la validación sigue guardada en el teléfono.` };
    case 413:
      return { text: 'Las fotos pesan demasiado para el servidor (413). Probable: fotos de alta resolución. Avisa a soporte.' };
    case 422:
      return { text: `El servidor dice que faltan datos o tienen formato inválido (${d}). Probable: la validación se guardó incompleta. Avisa a soporte.` };
    case 400:
    case 409:
      return { text: `El servidor rechazó la validación: ${d || 'datos inválidos'}. Se conserva en el teléfono para no perder la información; avisa a soporte.` };
    default:
      if (status >= 500) {
        return { text: `Error interno del servidor (${status}${d ? `: ${d}` : ''}). Probable: servidor en mantenimiento o saturado. Se reintentará automáticamente.` };
      }
      return { text: `Respuesta inesperada del servidor (${status}${d ? `: ${d}` : ''}).` };
  }
}

/** Explica un error de red/JS durante el envío. */
function explainNetworkFailure(err) {
  const msg = String(err?.message || err || '');
  if (/sin respuesta|abort|timeout/i.test(msg)) {
    return 'El servidor no respondió a tiempo (señal muy débil o saturada). Probable: hay "datos" pero casi sin velocidad. Acércate a una zona con mejor señal o usa Wi-Fi.';
  }
  if (/failed to fetch|network|load failed/i.test(msg) || err?.name === 'TypeError') {
    return 'No se pudo conectar con el servidor. Probable: sin internet real, VPN/proxy bloqueando o servidor caído. Prueba abrir otra página para verificar tu conexión.';
  }
  return `Error inesperado al enviar: ${msg || 'desconocido'}.`;
}

export function useOfflineSync({ workdayId = null, onSegmentSynced = null, onActionSynced = null } = {}) {
  const [isOnline, setIsOnline] = useState(() => navigator.onLine);
  const [pendingSegments, setPendingSegments] = useState(0);
  const [pendingActions, setPendingActions] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const [lastReport, setLastReport] = useState(null);
  const syncInProgress = useRef(false);
  const lastFailureAt = useRef(0);
  // Refs para callbacks: evitan recrear syncAll (y reiniciar el intervalo) en cada render
  const onSegmentSyncedRef = useRef(onSegmentSynced);
  const onActionSyncedRef = useRef(onActionSynced);
  onSegmentSyncedRef.current = onSegmentSynced;
  onActionSyncedRef.current = onActionSynced;

  // Conteo total de pendientes (lo que ve la UI)
  const pendingCount = pendingSegments + pendingActions;

  // ── Refresh de conteos ─────────────────────────────────────────────
  const refreshPendingCount = useCallback(async () => {
    try {
      const [segs, actions] = await Promise.all([
        storageService.getPendingInspectorSegments(workdayId),
        storageService.getPendingInspectorActions()
      ]);
      setPendingSegments(segs.length);
      setPendingActions(actions.length);
    } catch (err) {
      console.warn('[OfflineSync] Error leyendo pendientes:', err);
    }
  }, [workdayId]);

  // ── Sincronizar cola de SEGMENTOS PE ──────────────────────────────
  // `problems` acumula textos de diagnóstico para el reporte
  const syncSegments = useCallback(async (token, problems) => {
    const pending = await storageService.getPendingInspectorSegments(workdayId);
    if (pending.length === 0) return 0;

    let synced = 0;
    console.log(`🔄 [OfflineSync] Sincronizando ${pending.length} segmentos PE...`);

    for (const segment of pending) {
      try {
        const res = await inspectorService.registerPESegment(
          workdayId,
          segment.latitude,
          segment.longitude,
          segment.network_type,
          segment.incidences,
          segment.photo_blob,
          segment.recorded_at
        );

        if (res && res.success) {
          await storageService.deleteInspectorSegment(segment.id);
          synced++;
          if (onSegmentSyncedRef.current) onSegmentSyncedRef.current(segment);
        } else {
          problems.push(`Segmento PE #${segment.id}: No se pudo completar el registro en el servidor.`);
        }
      } catch (err) {
        console.warn(`⚠️ [OfflineSync] Segmento ${segment.id} aún sin señal o con error:`, err);
        problems.push(`Segmentos PE pendientes: ${explainNetworkFailure(err)}`);
        break;
      }
    }
    return synced;
  }, [workdayId]);

  // ── Sincronizar cola de ACCIONES (incidencias + validaciones PI) ──
  // Devuelve { synced, duplicates, failures:[{label,text}], notAttempted, authFailed }
  const syncActions = useCallback(async (token) => {
    const result = { synced: 0, duplicates: 0, failures: [], notAttempted: 0, authFailed: false };
    const pending = await storageService.getPendingInspectorActions();
    if (pending.length === 0) return result;

    console.log(`🔄 [OfflineSync] Sincronizando ${pending.length} acciones pendientes...`);

    for (let i = 0; i < pending.length; i++) {
      const action = pending[i];
      try {
        // Reconstruir FormData con los campos de texto
        const formData = new FormData();
        Object.entries(action.fields || {}).forEach(([key, val]) => {
          if (val !== null && val !== undefined) formData.append(key, String(val));
        });

        // Reconstruir y adjuntar fotos desde ArrayBuffer serializado
        const files = storageService.deserializeFiles(action.photos || []);
        const photoFieldName = action.photoFieldName || 'evidence_photos';
        files.forEach(file => formData.append(photoFieldName, file));

        // Timeout amplio (60s) y pocos reintentos: las fotos pesan y con señal débil
        // 15s no alcanzan, lo que dejaba la cola atorada indefinidamente.
        const res = await fetchWithRetry(
          action.endpoint,
          { method: action.method || 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData },
          2, 2000, 60000
        );

        if (res.ok) {
          await storageService.deleteInspectorAction(action.id);
          result.synced++;
          console.log(`✅ [OfflineSync] Acción sincronizada: ${action.label}`);
          if (onActionSyncedRef.current) onActionSyncedRef.current(action);
        } else {
          const errBody = await res.json().catch(() => ({}));
          console.warn(`⚠️ [OfflineSync] Acción ${action.label} rechazada por el servidor (${res.status}):`, errBody.detail);
          const detailText = detailToText(errBody.detail).toLowerCase();
          // Solo se borra si el servidor confirma que YA estaba procesada (duplicado).
          // Cualquier otro error se conserva en la cola para no perder información.
          // El backend antiguo devolvía 500 con el texto original dentro ("500: ... 400: Ya evaluaste"),
          // por eso también se acepta 500 si el detalle confirma que ya estaba procesada.
          const alreadyProcessed = [400, 409, 500].includes(res.status) &&
            ALREADY_PROCESSED_RE.test(detailText);
          if (alreadyProcessed) {
            await storageService.deleteInspectorAction(action.id);
            result.duplicates++;
            console.log(`🗑️ [OfflineSync] Acción removida de la cola (ya procesada por servidor): ${action.label}`);
          } else {
            const info = explainHttpFailure(res.status, errBody.detail);
            result.failures.push({ label: action.label, text: info.text });
            if (info.auth) {
              // Sesión expirada: detener, se reintentará tras re-login
              result.authFailed = true;
              result.notAttempted = pending.length - i - 1;
              break;
            }
          }
        }
      } catch (err) {
        console.warn(`⚠️ [OfflineSync] Acción ${action.label} falló (sin señal aún):`, err.message);
        result.failures.push({ label: action.label, text: explainNetworkFailure(err) });
        result.notAttempted = pending.length - i - 1;
        break;
      }
    }
    return result;
  }, []);

  // ── Función principal de sync ──────────────────────────────────────
  // opts.manual = true cuando el usuario presiona "Enviar ahora": siempre genera reporte.
  const syncAll = useCallback(async (opts = {}) => {
    const manual = !!opts.manual;
    // navigator.onLine no es confiable en móvil (puede ser true con señal inútil),
    // así que intentamos igual y fetchWithRetry decide si hay red real.
    if (syncInProgress.current) {
      if (manual) {
        setLastReport({
          at: new Date().toISOString(), level: 'info',
          title: 'Ya hay un envío en curso',
          lines: [{ level: 'info', text: 'Espera a que termine el envío actual; el resultado aparecerá aquí.' }]
        });
      }
      return;
    }

    // Reintento automático: respetar el enfriamiento tras una falla reciente
    if (!manual && Date.now() - lastFailureAt.current < AUTO_RETRY_COOLDOWN_MS) return;

    const pending = await storageService.getPendingInspectorActions();
    const segs = await storageService.getPendingInspectorSegments(workdayId);

    if (pending.length === 0 && segs.length === 0) {
      if (manual) {
        setLastReport({
          at: new Date().toISOString(), level: 'info',
          title: 'No hay nada pendiente en el teléfono',
          lines: [
            { level: 'info', text: 'La cola local está vacía. Si la pantalla mostraba pendientes, el contador estaba desactualizado: lo más probable es que esas validaciones YA se enviaron al servidor.' },
            { level: 'info', text: 'Verifica en la lista de recorridos que los segmentos aparezcan como validados.' }
          ]
        });
      }
      await refreshPendingCount();
      return;
    }

    const lines = [];
    const startedAt = new Date().toISOString();

    // ── Diagnóstico previo: sesión ──
    const token = localStorage.getItem('control_campo_token');
    if (!token) {
      setLastReport({
        at: startedAt, level: 'error',
        title: 'No se pudo enviar: no hay sesión iniciada',
        lines: [{ level: 'error', text: 'No se encontró el token de sesión en el teléfono. Cierra sesión y vuelve a entrar; las validaciones guardadas siguen en el teléfono (no se borran).' }]
      });
      return;
    }
    const expMs = getTokenExpiryMs(token);
    if (expMs && expMs < Date.now()) {
      setLastReport({
        at: startedAt, level: 'error',
        title: 'No se pudo enviar: tu sesión expiró',
        lines: [
          { level: 'error', text: `El token de sesión venció el ${new Date(expMs).toLocaleString()}. El servidor rechazaría los envíos.` },
          { level: 'warn', text: `Solución: cierra sesión, vuelve a entrar y presiona "Enviar ahora". Hay ${pending.length + segs.length} elemento(s) guardados que NO se pierden.` }
        ]
      });
      await refreshPendingCount();
      return;
    }
    if (!navigator.onLine) {
      lines.push({ level: 'warn', text: 'El teléfono reporta que no hay conexión; se intentará de todos modos.' });
    }

    syncInProgress.current = true;
    setIsSyncing(true);

    let report;
    try {
      const segProblems = [];
      const [syncedSegs, act] = await Promise.all([
        workdayId ? syncSegments(token, segProblems) : Promise.resolve(0),
        syncActions(token)
      ]);

      if (syncedSegs > 0) lines.push({ level: 'ok', text: `${syncedSegs} segmento(s) PE enviados correctamente.` });
      if (act.synced > 0) lines.push({ level: 'ok', text: `${act.synced} validación(es)/acción(es) enviadas correctamente.` });
      if (act.duplicates > 0) {
        lines.push({ level: 'info', text: `${act.duplicates} ya estaban registradas en el servidor (se enviaron antes pero el teléfono no lo había limpiado). Se quitaron de la cola; no hay que hacer nada más.` });
      }

      // Agrupar fallas por causa para no mostrar 31 líneas iguales
      const grouped = new Map();
      act.failures.forEach(f => {
        const g = grouped.get(f.text) || { count: 0, labels: [] };
        g.count++;
        if (g.labels.length < 3) g.labels.push(f.label);
        grouped.set(f.text, g);
      });
      grouped.forEach((g, text) => {
        lines.push({ level: 'error', text: `${g.count > 1 ? `(${g.count} casos, ej: ${g.labels.join(', ')}) ` : `${g.labels[0]}: `}${text}` });
      });
      segProblems.forEach(t => lines.push({ level: 'error', text: t }));
      if (act.notAttempted > 0) {
        lines.push({ level: 'warn', text: `${act.notAttempted} más quedaron sin intentar porque el envío se detuvo en la falla anterior. Siguen guardadas en el teléfono.` });
      }

      const hasErrors = act.failures.length > 0 || segProblems.length > 0;
      const remainingActions = (await storageService.getPendingInspectorActions()).length;
      if (hasErrors) {
        lines.push({ level: 'warn', text: `Pendientes en el teléfono: ${remainingActions}. No se borran; se reintentará automáticamente cada 30 s.` });
      }

      report = {
        at: startedAt,
        level: hasErrors ? 'error' : 'ok',
        title: hasErrors
          ? 'El envío tuvo problemas'
          : '✅ Todo se envió correctamente',
        lines
      };

      if (syncedSegs + act.synced > 0) {
        console.log(`🎉 [OfflineSync] Sync completo: ${syncedSegs} segmentos + ${act.synced} acciones`);
      }
    } catch (err) {
      console.error('[OfflineSync] Error general en sync:', err);
      report = {
        at: startedAt, level: 'error',
        title: 'Error inesperado al sincronizar',
        lines: [
          ...lines,
          { level: 'error', text: `${err?.message || err}. Probable: el almacenamiento local del teléfono está dañado o lleno. Tus datos no se borraron; avisa a soporte.` }
        ]
      };
    } finally {
      syncInProgress.current = false;
      setIsSyncing(false);
      await refreshPendingCount();
    }

    lastFailureAt.current = report.level === 'error' ? Date.now() : 0;

    // Los manuales siempre se muestran; los automáticos solo si todo salió bien
    // (los errores automáticos no reaparecen tras cerrarlos: se ven con el botón).
    if (manual || report.level === 'ok') setLastReport(report);
  }, [workdayId, syncSegments, syncActions, refreshPendingCount]);

  const syncNow = useCallback(() => syncAll({ manual: true }), [syncAll]);
  const clearReport = useCallback(() => setLastReport(null), []);

  // ── Listeners de conectividad ──────────────────────────────────────
  useEffect(() => {
    const handleOnline = () => {
      console.log('📶 [OfflineSync] Señal recuperada — sincronizando en 2s...');
      setIsOnline(true);
      setTimeout(() => syncAll(), 2000);
    };

    const handleOffline = () => {
      console.log('📴 [OfflineSync] Sin señal — datos se guardarán en cola offline');
      setIsOnline(false);
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') syncAll();
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    document.addEventListener('visibilitychange', handleVisibility);
    refreshPendingCount();

    // Pedir al navegador que NO borre IndexedDB bajo presión de almacenamiento
    try { navigator.storage?.persist?.(); } catch (e) { /* noop */ }

    // Reintento inmediato al montar y periódico cada 30s mientras haya pendientes
    // (el evento 'online' no se dispara si el equipo nunca perdió la conexión).
    syncAll();
    const interval = setInterval(() => syncAll(), 30000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, [syncAll, refreshPendingCount]);

  return {
    isOnline,
    pendingCount,
    pendingSegments,
    pendingActions,
    isSyncing,
    syncNow,
    lastReport,
    clearReport,
    refreshPendingCount,
  };
}
